/* eslint-disable */
import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material/";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Sidenav from "nav/Sidenav";
import Configurator from "nav/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  setActiveNavLink,
} from "context";

// Images
import brandWhite from "assets/images/logos/QD_icon_W.png";
import brandDark from "assets/images/logo-ct-dark.png";

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import SignIn from "layouts/authentication/sign-in";

// Firebase imports
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./Firebase";

// Cache Clearing
import withClearCache from "./clearCache";

function MainApp() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    cart,
    pageState,
    currentStore,
    activeNavLink,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { gradients } = colors;
  const [cartCount, setCartCount] = useState(false);

  const [user] = useAuthState(auth);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      // setMiniSidenav(dispatch, false); // <- Temporarily disabling this, because it is honestly an annoying user experience
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      // setMiniSidenav(dispatch, true); // <- Temporarily disabling this, because it is honestly an annoying user experience
      setOnMouseEnter(false);
    }
  };

  const [smallWindow, setSmallWindow] = useState(window.innerWidth < 1200);
  useEffect(() => {
    const handleResize = () => {
      const isSamller = window.innerWidth < 1200;
      setSmallWindow(isSamller);
      setMiniSidenav(dispatch, isSamller);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    if (currentStore.includes(activeNavLink)) {
      setActiveNavLink(dispatch, currentStore);
    } else {
      setActiveNavLink(dispatch, "");
    }
  }, [pageState]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname, pageState, currentStore]);

  useEffect(() => {
    if (cart.length > 0) {
      setCartCount(true);
    } else {
      setCartCount(false);
    }
  }, [cart]);

  useEffect(() => {
    document.addEventListener("scroll", () => {
      // setMiniSidenav(dispatch, true); // <- Temporarily disabling this, because it is honestly an annoying user experience
    });
  }, []);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor={
        cartCount > 0
          ? "success"
          : linearGradient(gradients.secondary.main, gradients.secondary.state)
      }
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="light"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small">shopping_cart</Icon>
      {cartCount && (
        <MDTypography variant="caption" color="light">
          <sub>{cart.length}</sub>
        </MDTypography>
      )}
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Material Dashboard 2"
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "loading" && (
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      )}
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="QD Storefront"
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        <Route exact path="*" element={!user ? <SignIn /> : <Dashboard />} />
      </Routes>
    </ThemeProvider>
  );
}

const ClearCacheComponent = withClearCache(MainApp);
function App() {
  return <ClearCacheComponent />;
}

export default App;
